import { OTLayout } from "@/components/layout/ot-layout";
import { useState } from "react";
import { Printer } from "lucide-react";

const CONDITION_PROFILES = [
  {
    condition: "Rheumatoid Arthritis",
    fatigueCause: "Inflammatory cytokines (TNF-α, IL-6), anaemia of chronic disease, pain, sleep disruption, deconditioning, depression",
    characteristics: ["Physical fatigue predominant", "Worsens with disease activity (DAS28 correlation)", "Morning stiffness component", "Improves with DMARD/biologic therapy"],
    assessmentTools: ["NRS/VAS fatigue (0–10)", "BRAF-MDQ (Bristol RA Fatigue Multi-Dimensional Questionnaire)", "FACIT-Fatigue", "RAID (Rheumatoid Arthritis Impact of Disease)"],
    interventions: ["COT Fatigue Management Programme (group or individual)", "Pacing and activity diary", "DMARD optimisation (refer to rheumatology nurse/consultant)", "Aerobic exercise (CSP/NICE NG100 recommendation)", "Sleep hygiene"],
    resources: ["NRAS fatigue resources", "Versus Arthritis fatigue guide", "ARUK/NRAS energy conservation booklet"],
  },
  {
    condition: "Systemic Lupus Erythematosus",
    fatigueCause: "Multi-factorial and distinct from RA: neuropsychiatric SLE, cognitive dysfunction, fibromyalgia overlap, anaemia, hypothyroidism, deconditioning, depression, medication side effects",
    characteristics: ["Often most disabling symptom in SLE (reported by 80–90% of patients)", "Cognitive fatigue (brain fog) is a key feature — distinct from physical fatigue", "Not always correlated with disease activity / SLEDAI", "Post-exertional malaise may occur", "Fluctuating and unpredictable"],
    assessmentTools: ["FACIT-Fatigue", "Chalder Fatigue Scale", "FSS (Fatigue Severity Scale)", "Cognitive testing: MoCA, ACE-R where cognitive fatigue suspected", "PHQ-9 / GAD-7 for mood"],
    interventions: ["Energy envelope management — stay within energy envelope (avoid boom-and-bust)", "Cognitive fatigue strategies: task-switching, brain breaks, written lists, memory aids", "Psychological referral (CBT for SLE fatigue — Chalder et al. evidence)", "Photoprotection (sun exposure worsens fatigue in SLE)", "Sleep hygiene — circadian rhythm disruption common"],
    resources: ["LUPUS UK fatigue factsheet", "LUPUS UK cognitive dysfunction leaflet", "Kidney Research UK / SLE Toolkit"],
  },
  {
    condition: "Ankylosing Spondylitis / axSpA",
    fatigueCause: "Inflammatory cytokines, sleep disruption (pain, nocturnal waking), anaemia, depression, deconditioning",
    characteristics: ["Often underreported", "BASFI and BASDAI include fatigue items", "Morning stiffness drives early-day fatigue", "Exercise-responsive (unlike SLE fatigue)"],
    assessmentTools: ["BASDAI item 1 (fatigue VAS)", "FACIT-Fatigue", "NRS fatigue"],
    interventions: ["Exercise programme (BASEX, aquatic therapy)", "Sleep positioning advice — firm mattress, low pillow", "Pacing around morning stiffness", "NSAID timing (night-time dose to improve sleep quality — discuss with prescriber)"],
    resources: ["NASS (National Axial Spondyloarthritis Society) fatigue resources", "NASS Back to Action programme"],
  },
  {
    condition: "Sjögren's Syndrome",
    fatigueCause: "Fatigue is one of the most disabling symptoms: autonomic dysfunction, sleep disruption, depression, fibromyalgia overlap, hypothyroidism (common comorbidity)",
    characteristics: ["Severe, often underrecognised", "Sleep architecture disruption common", "Cognitive fog frequent", "Limited pharmacological treatment for fatigue"],
    assessmentTools: ["ESSPRI (EULAR Sjögren's Syndrome Patient Reported Index) includes fatigue", "PROFAD fatigue scale", "FSS"],
    interventions: ["Cognitive fatigue strategies", "Energy management planning", "Sleep hygiene", "Psychological referral", "MDT review for comorbid hypothyroidism, fibromyalgia"],
    resources: ["BSSA (British Sjögren's Syndrome Association) resources"],
  },
];

const PACING_STEPS = [
  {
    step: "1. Establish a symptom diary",
    detail: "Record activity levels, fatigue ratings (0–10), pain, and sleep quality over 2 weeks. Identify patterns, peaks, and troughs.",
  },
  {
    step: "2. Calculate baseline",
    detail: "Identify the sustainable level of activity that does not cause a significant fatigue crash (the 'safe zone'). Start at 70% of what the patient thinks they can manage.",
  },
  {
    step: "3. Set consistent baseline activity",
    detail: "Perform the baseline activity consistently on good and bad days — do not do more on good days (boom-and-bust prevention).",
  },
  {
    step: "4. Build graded activity",
    detail: "Increase activity by no more than 10% per week. Introduce new activities one at a time. Monitor for post-exertional malaise the following day.",
  },
  {
    step: "5. Build rest into the plan",
    detail: "Schedule planned rest periods before fatigue develops — not reactive rest after crash. Rest periods are non-negotiable.",
  },
  {
    step: "6. Review and adjust",
    detail: "Weekly review against fatigue diary. Adjust baseline if needed — especially during disease flare.",
  },
];

const ENERGY_CONSERVATION = [
  { principle: "Plan", detail: "Plan the week ahead. Identify high-energy and low-energy tasks and distribute them evenly. Avoid clustering demanding tasks on the same day." },
  { principle: "Prioritise", detail: "Rank tasks by importance and necessity. Delegate or eliminate low-priority tasks. 'Good enough' is acceptable on low-energy days." },
  { principle: "Position", detail: "Work in a comfortable, supported position. Sit where possible. Reduce unnecessary reaching, bending, or carrying." },
  { principle: "Pacing", detail: "Work at a consistent, sustainable pace. Take micro-breaks every 20–30 minutes. Use the 4P principles framework consistently." },
];

const SLEEP_STRATEGIES = [
  "Consistent sleep and wake time — even at weekends",
  "Sleep hygiene: dark, cool, quiet room; avoid screens 1 hour before bed",
  "Avoid caffeine after 2pm",
  "Limit daytime napping to <20 minutes before 3pm",
  "Pain management before bed — consider timing of analgesia (discuss with prescribing team)",
  "Relaxation techniques: progressive muscle relaxation, mindfulness (link to Pain Management Programme if available)",
  "Referral to clinical psychology or sleep clinic if chronic insomnia",
  "CBT-I (Cognitive Behavioural Therapy for Insomnia) — refer if available; evidence base in inflammatory arthritis",
];

export default function FatigueManagement() {
  const [selectedCondition, setSelectedCondition] = useState(CONDITION_PROFILES[0].condition);
  const [printMode, setPrintMode] = useState(false);

  const currentProfile = CONDITION_PROFILES.find((p) => p.condition === selectedCondition)!;

  return (
    <OTLayout title="Fatigue Management">
      <div className="space-y-8">

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          <strong>COT Fatigue Management Programme:</strong> The College of Occupational Therapists' fatigue management approach is the standard OT framework for inflammatory arthritis fatigue — grounded in pacing, energy conservation, activity scheduling, and psychoeducation. Individual and group formats are both effective.
        </div>

        {/* Condition-Specific Fatigue Profiles */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Condition-Specific Fatigue Profiles</h2>
            <button
              onClick={() => setPrintMode(!printMode)}
              className="flex items-center gap-2 text-sm text-amber-700 border border-amber-300 px-3 py-1.5 rounded-lg hover:bg-amber-50 transition-colors"
            >
              <Printer className="h-4 w-4" />
              {printMode ? "Exit print view" : "Print patient handout"}
            </button>
          </div>

          {printMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6 print:block space-y-4">
              <div className="border-b border-amber-200 pb-4">
                <h3 className="font-bold text-slate-900 text-base">Managing Fatigue with {selectedCondition}</h3>
                <p className="text-sm text-slate-600 mt-1">Prepared by your occupational therapist. This handout summarises strategies to help you understand and manage your fatigue. Discuss these with your OT or rheumatology team.</p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-sm mb-2">The 4Ps — Energy Conservation</h4>
                <ul className="text-sm text-slate-700 space-y-1">
                  {ENERGY_CONSERVATION.map((e) => (
                    <li key={e.principle}><strong>{e.principle}:</strong> {e.detail}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-sm mb-2">Pacing — Key Principles</h4>
                <ul className="text-sm text-slate-700 space-y-1">
                  {PACING_STEPS.slice(0, 4).map((s) => (
                    <li key={s.step}>• <strong>{s.step.split(". ")[1]}:</strong> {s.detail}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-sm mb-2">Sleep Tips</h4>
                <ul className="text-sm text-slate-700 space-y-1">
                  {SLEEP_STRATEGIES.slice(0, 4).map((s) => (
                    <li key={s}>• {s}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-sm mb-2">Helpful Resources</h4>
                <ul className="text-sm text-slate-700 space-y-1">
                  {currentProfile.resources.map((r) => (
                    <li key={r}>• {r}</li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-slate-400 border-t border-amber-200 pt-3">This handout is for general guidance only. Always follow the advice of your healthcare team. RheumHub · rheumhub.co.uk</p>
              <button onClick={() => window.print()} className="text-xs text-amber-700 underline">Print this page</button>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-6">
            {CONDITION_PROFILES.map((p) => (
              <button
                key={p.condition}
                onClick={() => setSelectedCondition(p.condition)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCondition === p.condition ? "bg-amber-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-300"}`}
              >
                {p.condition}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Aetiology of Fatigue</h4>
              <p className="text-sm text-slate-700">{currentProfile.fatigueCause}</p>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Characteristics</h4>
              <ul className="space-y-1">
                {currentProfile.characteristics.map((c) => (
                  <li key={c} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="text-amber-500 mt-1 shrink-0">•</span>{c}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Assessment Tools</h4>
                <ul className="space-y-1">
                  {currentProfile.assessmentTools.map((a) => (
                    <li key={a} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="text-amber-500 mt-1 shrink-0">📋</span>{a}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">OT Interventions</h4>
                <ul className="space-y-1">
                  {currentProfile.interventions.map((i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="text-amber-500 mt-1 shrink-0">✓</span>{i}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Resources for Patients</h4>
              <ul className="flex flex-wrap gap-2">
                {currentProfile.resources.map((r) => (
                  <li key={r} className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full">{r}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Pacing */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Pacing — Graded Activity Framework</h2>
          <div className="space-y-3">
            {PACING_STEPS.map((s) => (
              <div key={s.step} className="bg-white rounded-xl border border-slate-200 p-4 flex gap-4">
                <div className="bg-amber-100 text-amber-700 rounded-lg px-3 py-1 text-xs font-bold h-fit shrink-0 whitespace-nowrap">
                  {s.step.split(".")[0]}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 text-sm">{s.step.split(". ")[1]}</h4>
                  <p className="text-sm text-slate-600 mt-1">{s.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Energy Conservation 4Ps */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Energy Conservation — The 4Ps</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ENERGY_CONSERVATION.map((e) => (
              <div key={e.principle} className="bg-white rounded-2xl border border-amber-200 p-5">
                <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center text-lg font-bold mb-3">
                  {e.principle[0]}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{e.principle}</h3>
                <p className="text-sm text-slate-600">{e.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Sleep hygiene */}
        <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h2 className="font-bold text-slate-900">Sleep Hygiene for Inflammatory Arthritis</h2>
          <p className="text-sm text-slate-500">Sleep disruption worsens fatigue and pain sensitivity (central sensitisation). Address sleep as part of all fatigue management programmes.</p>
          <ul className="space-y-2">
            {SLEEP_STRATEGIES.map((s) => (
              <li key={s} className="text-sm text-slate-700 flex items-start gap-2">
                <span className="text-amber-500 mt-1 shrink-0">•</span>{s}
              </li>
            ))}
          </ul>
        </section>

        {/* Psychological signpost */}
        <div className="bg-slate-900 rounded-2xl p-6 text-white">
          <h3 className="font-bold mb-2">Psychological Support Signposting</h3>
          <p className="text-slate-300 text-sm mb-4">Fatigue is significantly associated with depression and anxiety in inflammatory arthritis. Routine psychological screening is recommended. Refer to:</p>
          <ul className="text-sm text-slate-300 space-y-1">
            <li>• Clinical psychology via MDT referral</li>
            <li>• Pain Management Programme (PMP) for complex pain/fatigue</li>
            <li>• Talking Therapies / IAPT (NHS England primary care psychological therapies)</li>
            <li>• Wellbeing services via GP if wait for MDT psychology is long</li>
          </ul>
        </div>

      </div>
    </OTLayout>
  );
}
